import type { Auth as CPAuth } from "@storytell/cp-client";
import type { Named } from "@storytell/logger";
import type { User } from "firebase/auth";

// Firebase reports the persisted user only after re-validating it over the network, so every
// page load waits a full round trip before anything can reach the API. This module keeps its own
// copy of the signed-in session and starts the sync with it while that round trip runs.

export type SyncIdentity = Pick<User, "email" | "displayName" | "photoURL" | "getIdToken">;

type SessionStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

type RememberedSession = {
  uid: string;
  idToken: string;
  expiresAt: number;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
};

const REMEMBERED_SESSION_KEY = "st_auth_session";
// Firebase treats a token this close to expiry as expired and refreshes it before using it.
const TOKEN_REFRESH_BUFFER_MS = 30_000;

const tokenClaims = (idToken: string): { sub?: unknown; exp?: unknown } | null => {
  try {
    const payload = idToken.split(".")[1] ?? "";
    const claims: unknown = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof claims === "object" && claims !== null ? claims : null;
  } catch {
    return null;
  }
};

/**
 * Stores the session a later page load can sync with before Firebase is ready, replacing any
 * other user's. A token that doesn't belong to the user clears it instead. A null user is a
 * no-op: the remembered token itself is set before Firebase knows its user.
 */
export const rememberSession = (
  storage: SessionStorage,
  user: Pick<User, "uid" | "email" | "displayName" | "photoURL"> | null,
  idToken: string
) => {
  if (!user) return;
  const claims = tokenClaims(idToken);
  if (claims?.sub !== user.uid || typeof claims.exp !== "number") {
    storage.removeItem(REMEMBERED_SESSION_KEY);
    return;
  }
  const session: RememberedSession = {
    uid: user.uid,
    idToken,
    expiresAt: claims.exp * 1000,
    email: user.email,
    displayName: user.displayName,
    photoURL: user.photoURL,
  };
  storage.setItem(REMEMBERED_SESSION_KEY, JSON.stringify(session));
};

export const forgetSession = (storage: SessionStorage) => storage.removeItem(REMEMBERED_SESSION_KEY);

const stringOrNull = (value: unknown) => (typeof value === "string" ? value : null);

const readRememberedSession = (storage: SessionStorage, now: number): RememberedSession | null => {
  let stored: Partial<Record<keyof RememberedSession, unknown>>;
  try {
    const parsed: unknown = JSON.parse(storage.getItem(REMEMBERED_SESSION_KEY) ?? "null");
    stored = typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return null;
  }
  const { uid, idToken, expiresAt } = stored;
  if (typeof uid !== "string" || typeof idToken !== "string" || typeof expiresAt !== "number") return null;
  if (now > expiresAt - TOKEN_REFRESH_BUFFER_MS) return null;
  return {
    uid,
    idToken,
    expiresAt,
    email: stringOrNull(stored.email),
    displayName: stringOrNull(stored.displayName),
    photoURL: stringOrNull(stored.photoURL),
  };
};

/**
 * Registers the auth-state listener that keeps the token and the synced user current, and
 * syncs the remembered session before Firebase finishes re-validating it. A pending Google
 * redirect skips the remembered session: it can resolve to a different account, and the
 * redirect handling owns that sync. A failed redirect is logged and the listener still starts.
 *
 * Each signed-in uid is synced once: the listener skips a uid the early sync already took, one
 * a completed OAuth redirect already synced, and any user arriving while a token sign-in runs.
 * An early sync is dropped if the listener has since seen a sign-out or another user (Firebase
 * clears a deleted or disabled user during re-validation), or a token sign-in has started. An
 * early sync that fails before the listener's first call leaves the sync to the listener.
 */
export const listenForAuthChanges = async (deps: {
  storage: SessionStorage;
  redirectInProgress: boolean;
  refreshIdToken: (uid: string, forceRefresh?: boolean) => Promise<string>;
  processOAuthRedirect: () => Promise<boolean | undefined>;
  clearRedirectFlag: () => void;
  logger: Pick<Named, "error">;
  onAuthStateChanged: (listener: (user: User | null) => Promise<void>) => void;
  currentIdToken: () => Promise<string | undefined>;
  setToken: (token: string) => void;
  isProcessingTokenSignIn: () => boolean;
  sync: (user: SyncIdentity) => Promise<CPAuth.AuthenticatedUser | undefined>;
  setAuthenticatedUser: (authenticatedUser: CPAuth.AuthenticatedUser) => void;
  onUnauthenticated: () => Promise<void>;
}) => {
  // Setup runs during hydration; the token write waits until the hydrating render has finished.
  await Promise.resolve();

  let lastUserId: string | null = null;

  const session = deps.redirectInProgress ? null : readRememberedSession(deps.storage, Date.now());
  if (session) {
    lastUserId = session.uid;
    deps.setToken(session.idToken);
    const syncSession = async (): Promise<void> => {
      const authenticatedUser = await deps
        .sync({ ...session, getIdToken: (forceRefresh) => deps.refreshIdToken(session.uid, forceRefresh) })
        .catch(() => undefined);
      if (lastUserId !== session.uid || deps.isProcessingTokenSignIn()) return;
      if (authenticatedUser) {
        deps.setAuthenticatedUser(authenticatedUser);
      } else {
        lastUserId = null;
      }
    };
    void syncSession();
  }

  let didProcessOAuthRedirect = await deps.processOAuthRedirect().catch((error: unknown) => {
    deps.logger.error("oauth_redirect_failed", "processing the OAuth redirect failed", error);
    deps.clearRedirectFlag();
    return undefined;
  });

  deps.onAuthStateChanged(async (user) => {
    if (!user) {
      lastUserId = null;
      forgetSession(deps.storage);
      await deps.onUnauthenticated();
      return;
    }

    const token = await deps.currentIdToken();
    if (!token) {
      return;
    }
    deps.setToken(token);

    if (user.uid === lastUserId) {
      return;
    }

    lastUserId = user.uid;

    if (didProcessOAuthRedirect) {
      didProcessOAuthRedirect = false;
      return;
    }

    if (deps.isProcessingTokenSignIn()) {
      return;
    }

    const authenticatedUser = await deps.sync(user);
    if (authenticatedUser) {
      deps.setAuthenticatedUser(authenticatedUser);
    }
  });
};
